import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, CalendarCheck, Clock, MessageCircle, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../seo/SEO';
import { track } from '../lib/analytics';
import { suppressExitIntent } from '../lib/exitIntent';

const CALENDLY_URL = 'https://calendly.com/shipmaster/shipmaster-prep-consultation';

/**
 * Dedicated `/book` page.
 *
 * Why this page exists:
 *   Previously the homepage hero CTA scrolled to an inline Calendly iframe in
 *   the `#contact` section. Microsoft Clarity surfaced dead-clicks + rage-clicks
 *   on that CTA — users tapped "Book a Call", got a 3-5s loading iframe, and
 *   bailed. Moving Calendly to its own page (a) gets it out of the critical
 *   render path for the homepage, (b) lets us optimize the booking experience
 *   independently of the marketing page, and (c) gives us a clean conversion
 *   URL for Google Ads when a user is ready to commit.
 *
 * SEO: noindex — this is a conversion endpoint, not a content page. We don't
 *   want it competing with the homepage for "shipmaster" branded queries.
 */
export default function Book() {
  // Iframe is heavy (~400KB + handshake). Render a skeleton first frame so
  // the page paints instantly, then mount the iframe after first paint.
  const [iframeMounted, setIframeMounted] = useState(false);
  useEffect(() => {
    // requestIdleCallback would be ideal but Safari lacks it — a 100ms timeout
    // is the next-best thing and keeps LCP low.
    const t = setTimeout(() => setIframeMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Forward Calendly's postMessage events to dataLayer so GTM can fire the
  // primary `book_call` conversion to GA4 + Google Ads. Mirrors the logic
  // that used to live in the inline `#contact` Calendly embed.
  const bookedRef = useRef(false);
  useEffect(() => {
    const handler = (e) => {
      if (typeof e?.data !== 'object' || e.data === null) return;
      const evt = e.data.event;
      if (typeof evt !== 'string' || evt.indexOf('calendly') !== 0) return;
      track(`calendly_${evt.replace(/^calendly\./, '')}`, {
        calendly_event_uri: e.data?.payload?.event?.uri || null,
        calendly_invitee_uri: e.data?.payload?.invitee?.uri || null,
      });
      if (evt === 'calendly.event_scheduled' && !bookedRef.current) {
        bookedRef.current = true;
        // Primary conversion. value=500 is a soft LTV signal so Smart Bidding
        // ranks booked calls above raw form submits.
        track('book_call', {
          value: 500,
          currency: 'USD',
          calendly_event_uri: e.data?.payload?.event?.uri || null,
        });
        suppressExitIntent();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const calendlySrc = `${CALENDLY_URL}?embed_domain=${
    typeof window !== 'undefined' ? window.location.hostname : ''
  }&embed_type=Inline&hide_gdpr_banner=1&primary_color=4f46e5`;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Book a 15-Minute Call"
        description="Talk to a real human at Shipmaster Fulfillment. Pick a 15-minute slot — we'll review your fulfillment setup and put together a custom quote."
        noindex={true}
      />
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 font-medium transition-colors mb-6"
          data-testid="book-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10">
          {/* Left rail — context + trust */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
              15-minute call
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
              Let&apos;s talk through your fulfillment.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Pick a time on the right. We&apos;ll review your current setup, ballpark your savings,
              and tell you straight up whether we&apos;re a fit.
            </p>

            <ul className="mt-7 space-y-4">
              <BookBullet
                icon={Clock}
                title="15 minutes, no pitch"
                body="We respect your time. No 45-minute discovery decks."
              />
              <BookBullet
                icon={MessageCircle}
                title="Talk to a real human"
                body="You'll meet someone on our ops team — not an SDR reading from a script."
              />
              <BookBullet
                icon={CalendarCheck}
                title="Walk away with a number"
                body="Get a ballpark quote on the call. Detailed quote within 24 hours."
              />
              <BookBullet
                icon={ShieldCheck}
                title="Zero pressure"
                body="If we're not a fit we'll tell you (and recommend who might be)."
              />
            </ul>

            <p className="mt-8 text-sm text-slate-500">
              Not ready to commit to a call?{' '}
              <Link
                to="/#contact"
                className="font-semibold text-indigo-600 hover:underline"
                data-testid="book-prefer-form-link"
              >
                Send us your details instead
              </Link>{' '}
              &mdash; we&apos;ll reply within 24 hours.
            </p>
          </div>

          {/* Right rail — Calendly iframe */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-3 sm:p-4">
              <div
                style={{ minWidth: '320px', width: '100%', height: '720px' }}
                data-testid="book-calendly-wrap"
              >
                {iframeMounted ? (
                  <iframe
                    title="Schedule a meeting with Shipmaster"
                    src={calendlySrc}
                    style={{ minWidth: '320px', width: '100%', height: '720px', border: '0' }}
                    loading="lazy"
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="w-full h-full rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center"
                  >
                    <div className="text-slate-400 text-sm">Loading scheduler…</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function BookBullet({ icon: Icon, title, body }) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
        <Icon className="w-5 h-5" strokeWidth={2} />
      </span>
      <div>
        <p className="text-base font-bold text-slate-900">{title}</p>
        <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
